import { useActiveCircleId } from '@/hooks/use-active-circle';
import { EnumTheme } from '@calm/evexia-analytics';
import usePrefersDarkMode from '@rhg/hooks/use-prefers-dark-mode';
import useAuth from '@rhg/libs/auth0/use-auth';
import { useMemo, useContext, createContext, ReactNode, useState } from 'react';

export interface BaseProperties {
    IsConcierge: boolean;
    IsAuthenticated: boolean;
    Theme: EnumTheme;
}

export interface ConciergePermissionsReport {
    circleId: number;
    canCreateConcierge: boolean;
    isPermissionsSettled: boolean;
}

interface AnalyticsBaseContextValue {
    baseProperties: BaseProperties;
    /** Auth has loaded and the active circle's concierge permissions are known. Events wait for this. */
    isReady: boolean;
    /** Auth has loaded. User properties and super properties wait only for this, so the account sync and circle fetch that set them can finish. */
    isIdentityReady: boolean;
}

const AnalyticsBaseContext = createContext<AnalyticsBaseContextValue>({
    baseProperties: {
        IsConcierge: false,
        IsAuthenticated: false,
        Theme: EnumTheme.Light,
    },
    isReady: false,
    isIdentityReady: false,
});

const ConciergePermissionsReportContext = createContext<
    (report: ConciergePermissionsReport) => void
>(() => {});

/**
 * The circle query must be first observed below `AnalyticsProvider`, where
 * `setUserProfile` works, so the concierge permissions are read there and
 * reported up here instead of being read in this provider.
 */
export function AnalyticsBaseContextProvider({
    children,
}: {
    children: ReactNode;
}) {
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const { activeCircleId } = useActiveCircleId();
    const prefersDarkMode = usePrefersDarkMode();
    const [report, setReport] = useState<ConciergePermissionsReport>();

    const current = report?.circleId === activeCircleId ? report : undefined;

    const isPermissionsSettled = !!current?.isPermissionsSettled;
    const isConcierge = isPermissionsSettled && !!current?.canCreateConcierge;
    const isIdentityReady = !isAuthLoading;
    const isReady = isPermissionsSettled && isIdentityReady;

    const baseProperties = useMemo<BaseProperties>(
        () => ({
            IsConcierge: isConcierge,
            IsAuthenticated: isAuthenticated,
            Theme: prefersDarkMode ? EnumTheme.Dark : EnumTheme.Light,
        }),
        [isConcierge, isAuthenticated, prefersDarkMode]
    );

    const value = useMemo(
        () => ({ baseProperties, isReady, isIdentityReady }),
        [baseProperties, isReady, isIdentityReady]
    );
    return (
        <ConciergePermissionsReportContext.Provider value={setReport}>
            <AnalyticsBaseContext.Provider value={value}>
                {children}
            </AnalyticsBaseContext.Provider>
        </ConciergePermissionsReportContext.Provider>
    );
}

export function useAnalyticsBaseContext() {
    return useContext(AnalyticsBaseContext);
}

export function useReportConciergePermissions() {
    return useContext(ConciergePermissionsReportContext);
}
